import { useState, useRef, useCallback, useEffect } from 'react';

import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';

import type { QueryNL2CypherPayload, AssistantMessage } from '@atlassian/twg-query-builder';

import { getConfig } from '../../../../../../config';
import type { Message } from '../../../../../contexts/AsideContext';
import { SelectedSiteCloudIdAtom, CypherQueryAtom } from '../../../../../state/LabsAtoms';
import type { CyphieMode } from '../../../../../state/LabsAtoms';
import { requestNL2Cypher } from '../../../../../utils/requestNL2Cypher';
import { messages } from './messages';
import {
	buildNextUserMessage,
	formatResponse,
	messagesToApiFormat,
	type ChatLLMResponse,
} from './utils';

const MODEL_CONFIGS: Record<CyphieMode, QueryNL2CypherPayload['model']> = {
	default: { family: 'claude', version: 'latest' },
	reasoning: { family: 'gpt', version: '4.1-2025-04-14' },
};

const EXTERNAL_MODEL_CONFIGS: Record<CyphieMode, QueryNL2CypherPayload['model']> = {
	default: { family: 'gpt', version: 'gpt-5.6-luna' },
	reasoning: { family: 'gpt', version: 'gpt-5.6-sol' },
};

const systemPrompt = `You are Cyphie, a helpful AI assistant for Teamwork Graph. You help users write performant Cypher queries. Be concise but informative.
Use user:IdentityUser {ari: $me } in place of a full text ari if the ari in question is the user's ari.
We prefer variables over inline aris.
We prefer unions over option matches, where appropriate.
We like to use (datetime() - duration('P7D')) over explicit datetime timestamps`;

export function useNL2Cypher(external = false): {
	sendMessage: (
		userMessage: string,
		conversationHistory: Message[],
		mode?: CyphieMode,
	) => Promise<ChatLLMResponse>;
	cancel: () => void;
	isLoading: boolean;
	error: string | null;
} {
	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	// Get cloudId from state, fallback to default
	const selectedCloudId = useAtomValue(SelectedSiteCloudIdAtom);
	const cloudId = external ? selectedCloudId : selectedCloudId || getConfig().defaultSiteCloudId;

	useEffect(() => () => abortControllerRef.current?.abort(), []);

	const currentCypherQuery = useAtomValue(CypherQueryAtom)?.trim() ?? '';

	const sendMessage = useCallback(
		async (
			userMessage: string,
			conversationHistory: Message[],
			mode: CyphieMode = 'default',
		): Promise<ChatLLMResponse> => {
			if (!cloudId) {
				return { content: formatMessage(messages.selectSite) };
			}
			abortControllerRef.current?.abort('Starting new query');
			setIsLoading(true);
			setError(null);

			const payloadMessages = [
				...messagesToApiFormat(conversationHistory),
				{
					role: 'user',
					content: [{ type: 'text', text: buildNextUserMessage(currentCypherQuery, userMessage) }],
				} satisfies AssistantMessage,
			];

			const payload: QueryNL2CypherPayload = {
				model: (external ? EXTERNAL_MODEL_CONFIGS : MODEL_CONFIGS)[mode],
				messages: payloadMessages,
				params: {
					maxTokens: 100000,
					temperature: 0.7,
				},
				system: systemPrompt,
				thinking: 'none',
			};

			const { controller, execute } = requestNL2Cypher(payload, cloudId, {
				cypherOnly: true,
				external,
				additionalHeaders: {
					...(mode === 'reasoning' ? { 'x-generate-cypher-by-anatomy': 'true' } : {}),
				},
			});
			abortControllerRef.current = controller;

			const res = await execute;
			if (!res.success) {
				const errorMessage = res.error?.message ?? 'An unexpected error occurred';
				setError(errorMessage);
				setIsLoading(false);
				return { content: `Sorry, I encountered an error: ${errorMessage}. Please try again.` };
			}
			setIsLoading(false);
			return formatResponse(res.data);
		},
		[cloudId, currentCypherQuery, external, formatMessage],
	);

	const cancel = useCallback((): void => {
		abortControllerRef.current?.abort('User cancelled');
	}, []);

	return {
		sendMessage,
		cancel,
		isLoading,
		error,
	};
}
